import { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Phone, 
  MessageSquare, 
  Video, 
  CalendarDays,
  Clock,
  CheckCircle,
  ArrowRight,
  ShoppingCart,
  ExternalLink,
  Headphones,
  Monitor,
  Smartphone,
  Globe,
  Users,
  Star,
  Zap,
  Heart,
  Target,
  BookOpen,
  Sparkles
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for dynamic session types
const iconMap = {
  phone: Phone,
  video: Video,
  message: MessageSquare,
  headphones: Headphones,
  monitor: Monitor,
  smartphone: Smartphone,
  globe: Globe,
  users: Users,
  star: Star,
  zap: Zap,
  heart: Heart,
  target: Target,
  book: BookOpen,
  sparkles: Sparkles,
};

const getIconComponent = (iconName) => {
  return iconMap[iconName] || Phone;
};

const timeSlots = [
  '09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'
];

export default function CoachingPage() {
  const { currentTheme } = useTheme();
  const { user } = useAuth();
  const [availableSlots, setAvailableSlots] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Request form state
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    session_type: '',
    preferred_date: '',
    preferred_time: '',
    topic: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [slotsRes, sessionsRes, typesRes] = await Promise.all([
        axios.get(`${API}/coaching/slots`),
        axios.get(`${API}/coaching/my-sessions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/coaching/sessions`)
      ]);
      setAvailableSlots(slotsRes.data || []);
      setMySessions(sessionsRes.data || []);
      setSessionTypes(typesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/coaching/request`, formData, { withCredentials: true });
      toast.success('Coaching session requested! We\'ll be in touch soon.');
      setFormData({
        session_type: '',
        preferred_date: '',
        preferred_time: '',
        topic: '',
        notes: ''
      });
      setSelectedDate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBookSlot = async (slot) => {
    const topic = prompt('What would you like to discuss in this session?');
    if (!topic) return;

    try {
      const formData = new FormData();
      formData.append('topic', topic);
      
      await axios.post(`${API}/coaching/book/${slot.slot_id}`, formData, { withCredentials: true });
      toast.success('Session booked successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book slot');
    }
  };

  const handlePurchaseClick = (session) => {
    if (session.purchase_link) {
      window.open(session.purchase_link, '_blank');
    } else {
      toast.info('Purchase link not configured. Please contact us to purchase.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#2A9D8F';
      case 'pending': return '#E9C46A';
      case 'completed': return currentTheme.primary;
      case 'cancelled': return '#E76F51';
      default: return currentTheme.muted;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.background }}>
      <Navbar />
      
      <main className="pt-28 pb-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-12">
            <h1 className="font-display text-4xl md:text-5xl font-medium mb-4" style={{ color: currentTheme.foreground }}>
              Coaching Sessions
            </h1>
            <p className="font-body text-lg opacity-70 max-w-2xl">
              Book a personal coaching session to accelerate your journey to freedom. Choose from phone, chat, or video sessions.
            </p>
          </div>

          <Tabs defaultValue="request" className="space-y-8">
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="request" data-testid="coaching-tab-request">Request Session</TabsTrigger>
              <TabsTrigger value="available" data-testid="coaching-tab-available">Available Slots</TabsTrigger>
              <TabsTrigger value="my-sessions" data-testid="coaching-tab-my-sessions">My Sessions</TabsTrigger>
            </TabsList>

            {/* Request Session Tab */}
            <TabsContent value="request">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Session Types */}
                <div>
                  <h2 className="font-display text-2xl font-medium mb-6" style={{ color: currentTheme.foreground }}>
                    Choose Session Type
                  </h2>
                  <div className="space-y-4">
                    {sessionTypes.map((session) => {
                      const IconComponent = getIconComponent(session.icon);
                      return (
                        <div
                          key={session.id}
                          className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
                            formData.session_type === session.id 
                              ? 'border-current shadow-lg' 
                              : 'border-transparent'
                          }`}
                          style={{ 
                            backgroundColor: formData.session_type === session.id ? `${currentTheme.primary}10` : 'white',
                            borderColor: formData.session_type === session.id ? currentTheme.primary : currentTheme.border
                          }}
                        >
                          <button
                            onClick={() => setFormData({ ...formData, session_type: session.id })}
                            className="w-full text-left"
                            data-testid={`session-type-${session.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <div 
                                className="w-14 h-14 rounded-xl flex items-center justify-center"
                                style={{ backgroundColor: `${currentTheme.primary}20` }}
                              >
                                <IconComponent className="w-7 h-7" style={{ color: currentTheme.primary }} />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-body font-medium text-lg" style={{ color: currentTheme.foreground }}>
                                  {session.title}
                                </h3>
                                <p className="font-body text-sm opacity-60">{session.description}</p>
                                {session.duration && (
                                  <p className="font-body text-xs opacity-50 mt-1 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {session.duration}
                                  </p>
                                )}
                              </div>
                              {formData.session_type === session.id && (
                                <CheckCircle className="w-6 h-6" style={{ color: currentTheme.primary }} />
                              )}
                            </div>
                          </button>
                          
                          {/* Features */}
                          {session.features && session.features.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {session.features.slice(0, 3).map((feature, i) => (
                                <span 
                                  key={i}
                                  className="text-xs px-2 py-1 rounded-full flex items-center gap-1"
                                  style={{ backgroundColor: `${currentTheme.secondary}15`, color: currentTheme.secondary }}
                                >
                                  <CheckCircle className="w-3 h-3" />
                                  {feature}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* Purchase Section */}
                          <div className="mt-4 pt-4 border-t flex items-center justify-between" style={{ borderColor: `${currentTheme.border}50` }}>
                            <div>
                              <span className="font-display text-2xl font-bold" style={{ color: currentTheme.primary }}>
                                {session.price || '$99'}
                              </span>
                              <span className="text-sm opacity-60 ml-1">/ session</span>
                            </div>
                            <Button
                              onClick={() => handlePurchaseClick(session)}
                              className="gap-2"
                              style={{ backgroundColor: currentTheme.secondary }}
                              data-testid={`purchase-btn-${session.id}`}
                            >
                              <ShoppingCart className="w-4 h-4" />
                              {session.button_text || 'Purchase Now'}
                              {session.purchase_link && <ExternalLink className="w-3 h-3" />}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Request Form */}
                <div 
                  className="rounded-2xl p-8"
                  style={{ backgroundColor: 'white', boxShadow: '0 4px 30px rgba(0,0,0,0.05)' }}
                >
                  <h2 className="font-display text-2xl font-medium mb-6" style={{ color: currentTheme.foreground }}>
                    Session Details
                  </h2>
                  <form onSubmit={handleSubmitRequest} className="space-y-6">
                    <div className="space-y-2">
                      <Label className="font-body font-medium">Preferred Date</Label>
                      <div className="border rounded-xl p-4" style={{ borderColor: currentTheme.border }}>
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => {
                            setSelectedDate(date);
                            setFormData({ 
                              ...formData, 
                              preferred_date: date?.toISOString().split('T')[0] || ''
                            });
                          }}
                          disabled={(date) => date < new Date()}
                          className="rounded-md"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="font-body font-medium">Preferred Time</Label>
                      <Select 
                        value={formData.preferred_time} 
                        onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                      >
                        <SelectTrigger data-testid="preferred-time-select">
                          <SelectValue placeholder="Select a time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="font-body font-medium">Topic</Label>
                      <Input
                        placeholder="What would you like to discuss?"
                        value={formData.topic}
                        onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                        required
                        data-testid="coaching-topic"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="font-body font-medium">Additional Notes</Label>
                      <Textarea
                        placeholder="Any specific questions or areas of focus..."
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={4}
                        data-testid="coaching-notes"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12"
                      style={{ backgroundColor: currentTheme.primary }}
                      disabled={submitting || !formData.session_type || !formData.preferred_date || !formData.preferred_time || !formData.topic}
                      data-testid="submit-coaching-request"
                    >
                      {submitting ? 'Submitting...' : 'Request Session'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </div>
              </div>
            </TabsContent>

            {/* Available Slots Tab */}
            <TabsContent value="available">
              {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-40 rounded-2xl" />
                  ))}
                </div>
              ) : availableSlots.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {availableSlots.map((slot) => (
                    <div
                      key={slot.slot_id}
                      className="rounded-2xl p-6 card-hover"
                      style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                      data-testid={`slot-${slot.slot_id}`}
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${currentTheme.primary}20` }}
                        >
                          <CalendarDays className="w-6 h-6" style={{ color: currentTheme.primary }} />
                        </div>
                        <div>
                          <div className="font-body font-medium" style={{ color: currentTheme.foreground }}>
                            {slot.date}
                          </div>
                          <div className="flex items-center gap-1 text-sm opacity-60">
                            <Clock className="w-4 h-4" />
                            {slot.time}
                          </div>
                        </div>
                      </div>
                      <div 
                        className="px-3 py-1 rounded-full text-sm font-medium capitalize inline-flex items-center gap-1 mb-4"
                        style={{ backgroundColor: `${currentTheme.secondary}20`, color: currentTheme.secondary }}
                      >
                        {slot.session_type === 'phone' && <Phone className="w-4 h-4" />}
                        {slot.session_type === 'chat' && <MessageSquare className="w-4 h-4" />}
                        {slot.session_type === 'video' && <Video className="w-4 h-4" />}
                        {slot.session_type}
                      </div>
                      <Button
                        onClick={() => handleBookSlot(slot)}
                        className="w-full"
                        style={{ backgroundColor: currentTheme.primary }}
                      >
                        Book This Slot
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <CalendarDays className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
                    No available slots
                  </h3>
                  <p className="font-body opacity-60">
                    Request a custom session time using the form above.
                  </p>
                </div>
              )}
            </TabsContent>

            {/* My Sessions Tab */}
            <TabsContent value="my-sessions">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-24 rounded-xl" />
                  ))}
                </div>
              ) : mySessions.length > 0 ? (
                <div className="space-y-4">
                  {mySessions.map((session) => (
                    <div
                      key={session.session_id}
                      className="rounded-2xl p-6 flex items-center justify-between"
                      style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                      data-testid={`my-session-${session.session_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-14 h-14 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${currentTheme.primary}20` }}
                        >
                          {session.session_type === 'phone' && <Phone className="w-7 h-7" style={{ color: currentTheme.primary }} />}
                          {session.session_type === 'chat' && <MessageSquare className="w-7 h-7" style={{ color: currentTheme.primary }} />}
                          {session.session_type === 'video' && <Video className="w-7 h-7" style={{ color: currentTheme.primary }} />}
                        </div>
                        <div>
                          <h3 className="font-body font-medium text-lg" style={{ color: currentTheme.foreground }}>
                            {session.topic}
                          </h3>
                          <div className="flex items-center gap-4 mt-1 text-sm opacity-60">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="w-4 h-4" />
                              {session.preferred_date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {session.preferred_time}
                            </span>
                          </div>
                        </div>
                      </div>
                      <span 
                        className="px-4 py-2 rounded-full text-sm font-medium capitalize"
                        style={{ 
                          backgroundColor: `${getStatusColor(session.status)}20`,
                          color: getStatusColor(session.status)
                        }}
                      >
                        {session.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <CalendarDays className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
                    No sessions yet
                  </h3>
                  <p className="font-body opacity-60">
                    Book your first coaching session to get started!
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
}
